/*
 * Size suggestions.
 *
 * Clicking a size that has no stock opens a drawer with products that do have
 * that size, closest color first, each addable to the cart in that size.
 *
 * Results come from the storefront filters the collection pages already use,
 * requested through the Section Rendering API against a collection URL:
 *
 *   /collections/all?section_id=size-suggestions
 *     &filter.v.option.maattabel=L&filter.v.availability=1
 *     &filter.p.m.product.color=Blauw
 *
 * Shopify applies filter.v.* per variant, so size plus availability means
 * "this exact size is in stock", not "this product has stock somewhere".
 *
 * One query cannot express "same color AND same pack, or else same color, or
 * else same type", because repeating a filter is an OR and there is no
 * relevance order in the response. So the tiers below run in order, narrow
 * first, and stop as soon as enough products have come back. The merged set is
 * then sorted on how many color groups it shares with the product that was
 * clicked, which is the priority the buyer asked for.
 */
if (!customElements.get('size-suggestions')) {
  /*
   * Narrow to wide. `color`, `pack` and `type` say which filters a tier adds on
   * top of size + availability, which every tier has. A tier is skipped when
   * the product carries no data for one of its filters, and duplicate queries
   * are skipped as well, so a product without a pack simply starts at the
   * second tier.
   *
   * Category is deliberately not in here. It is the department, and it is
   * added to every single query instead of being one more thing that gets
   * relaxed on the way down: a womens 2-pack must never widen into mens
   * boxers, no matter how thin the results get. The last tiers drop the
   * product type as well, so the wide one is only safe while the department
   * still holds, which is what `needsCategory` marks.
   */
  const TIERS = [
    { color: true, pack: true, type: true },
    { color: true, type: true },
    { color: true, needsCategory: true },
    { pack: true, type: true },
    { type: true },
  ];

  class SizeSuggestions extends HTMLElement {
    constructor() {
      super();
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.close = this.close.bind(this);
      /* Keyed by context handle + size, so re-opening the same one is free. */
      this.cache = new Map();
    }

    connectedCallback() {
      this.panel = this.querySelector('[data-size-suggestions-panel]');
      this.backdrop = this.querySelector('[data-size-suggestions-backdrop]');
      this.body = this.querySelector('[data-size-suggestions-body]');
      this.heading = this.querySelector('[data-size-suggestions-heading]');
      this.empty = this.querySelector('[data-size-suggestions-empty]');
      this.limit = parseInt(this.dataset.limit, 10) || 8;

      /*
       * Delegated on the document. Every list this reacts to is re-rendered
       * behind its back: the product section on a variant change, the quick
       * buy popup on every open. A listener on the lists themselves would not
       * survive any of that.
       */
      document.addEventListener('click', this.onDocumentClick, true);
      document.addEventListener('keydown', this.onKeydown);
      this.backdrop?.addEventListener('click', this.close);
      this.querySelector('[data-size-suggestions-close]')?.addEventListener('click', this.close);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick, true);
      document.removeEventListener('keydown', this.onKeydown);
      this.controller?.abort();
      document.body.classList.remove('size-suggestions-is-open');
    }

    /* Triggers ----------------------------------------------------------- */

    /*
     * A sold out size can be clicked in three places, and they agree on
     * nothing: the product page has radios keyed by option value, the sticky
     * add to cart bar has one row per variant keyed by variant id, and the
     * quick views have buttons carrying the variant title. Each is resolved to
     * the same answer here, "which size was this and is it sold out", and
     * everything after this point is shared.
     *
     * Captured on the way down, because the quick view is behind a
     * pointer-events:none rule and the sticky bar rows stop propagation, so
     * waiting for the bubble would miss both.
     */
    onDocumentClick(event) {
      const target = event.target;
      /* Clicks inside the drawer are its own, never a size being picked. */
      if (this.contains(target)) return;

      const found =
        this.fromVariantRadio(target) ||
        this.fromQuickView(target) ||
        this.fromStickyBar(target) ||
        this.fromRebuyWidget(target);

      if (!found) return;

      event.preventDefault();
      event.stopPropagation();
      this.open(found.size, found.context);
    }

    /* Product page: the size buttons. */
    fromVariantRadio(target) {
      const option = target.closest('[data-option]');
      if (!option) return null;

      const input = option.querySelector('[data-option-input]');
      if (!input || input.type !== 'radio') return null;

      const context = this.contextFor(option);
      const group = option.closest('[data-option-group]');
      const index = group ? parseInt(group.dataset.optionGroup, 10) : 0;
      if (index !== this.sizeIndex(context)) return null;

      if (this.isAvailable(input.value, context)) return null;
      return { size: input.value, context };
    }

    /*
     * Quick view on a product card, and the quick buy popup. Both mark a sold
     * out size with quick-view-unavailable and carry the variant title, which
     * for these products is the size.
     */
    fromQuickView(target) {
      const option = target.closest('.quick-view__option');
      if (!option) return null;
      if (!option.querySelector('.quick-view-unavailable')) return null;

      const context = this.contextFor(option);
      const title = option.dataset.title || '';
      /* A title with a slash is a multi option variant, size last. */
      const size = title.includes('/') ? title.split('/').pop().trim() : title.trim();
      if (!size) return null;

      return { size, context };
    }

    /* Product page: the size list in the sticky add to cart bar. */
    fromStickyBar(target) {
      const row = target.closest('li');
      const input = row?.querySelector('[data-atc-option]');
      if (!input || !input.disabled) return null;

      const context = this.contextFor(input);
      const variant = this.getVariants().find((entry) => String(entry.id) === input.value);
      const size = variant ? variant.options[this.sizeIndex(context)] : null;
      if (!size) return null;

      return { size, context };
    }

    /*
     * Product page: the size buttons inside the Rebuy "buy more, save more"
     * widget, which stands in for the theme's own picker whenever it is on the
     * page. Rebuy's swatches say nothing about stock, so the sold out ones are
     * marked by rebuy-bmsm.js from the same variant data read below; as with
     * the theme's picker, the label is click-through and the swatch around it
     * is what the click lands on.
     */
    fromRebuyWidget(target) {
      const swatch = target.closest('.rebuy-bmsm-unavailable');
      if (!swatch) return null;

      const input = swatch.querySelector('input[type="radio"]');
      const size = input ? input.value.trim() : '';
      if (!size) return null;

      /*
       * Rebuy's markup carries no context of its own, and the widget is always
       * about the product the page is about, so the page's own picker is where
       * the context comes from.
       */
      const context =
        this.contextFor(swatch) || document.querySelector('[data-size-suggestions-context]');
      if (!context) return null;

      return { size, context };
    }

    /*
     * Which product the click was about. The quick views show a different
     * product than the page behind them, so the answer is the nearest context
     * in the DOM rather than anything held on this element.
     */
    contextFor(element) {
      return element.closest('[data-size-suggestions-context]');
    }

    sizeIndex(context) {
      return parseInt(context?.dataset.sizeOptionIndex, 10) || 0;
    }

    split(context, key) {
      const value = context?.dataset[key];
      return value ? value.split('|').map((part) => part.trim()).filter(Boolean) : [];
    }

    /*
     * Availability of a size on the product page, read from the variant JSON
     * the product section already prints. The other options keep whatever is
     * selected, so on a product with a second option the answer is about the
     * combination the customer is actually looking at. Falling back to "any
     * variant with this size" keeps single option products working when
     * nothing is checked yet.
     */
    isAvailable(value, context) {
      const variants = this.getVariants();
      if (!variants.length) return true;

      const index = this.sizeIndex(context);
      const selected = Array.from(
        document.querySelectorAll('variant-radios [data-option-input]:checked')
      ).map((input) => input.value);
      selected[index] = value;

      const match = variants.find((variant) =>
        variant.options.every((option, position) => {
          if (position === index) return option === value;
          if (!selected[position]) return true;
          return option === selected[position];
        })
      );

      if (match) return Boolean(match.available);
      return variants.some((variant) => variant.options[index] === value && variant.available);
    }

    getVariants() {
      if (this.variants) return this.variants;
      const json = document.querySelector('variant-radios script[type="application/json"]');
      try {
        this.variants = json ? JSON.parse(json.textContent) : [];
      } catch (error) {
        this.variants = [];
      }
      return this.variants;
    }

    /* Panel -------------------------------------------------------------- */

    open(size, context) {
      const key = `${context?.dataset.handle || ''}::${size}`;
      /*
       * A click on a label reaches this twice: once for the label itself and
       * once for the synthetic click it sends to its input. Without this the
       * same size would be fetched twice.
       */
      if (this.openKey === key && document.body.classList.contains('size-suggestions-is-open')) {
        return;
      }
      this.openKey = key;

      this.previousFocus = document.activeElement;
      this.panel.hidden = false;
      this.backdrop.hidden = false;
      /* Next frame, so the transition has a state to move away from. */
      requestAnimationFrame(() => document.body.classList.add('size-suggestions-is-open'));
      this.panel.focus();

      if (this.heading) {
        this.heading.textContent = `${this.heading.dataset.label} - ${size}`;
      }

      this.load(size, context, key);
    }

    close() {
      this.openKey = null;
      document.body.classList.remove('size-suggestions-is-open');
      this.controller?.abort();
      /* Kept in the DOM until the panel has slid out, then taken out of it. */
      window.setTimeout(() => {
        if (document.body.classList.contains('size-suggestions-is-open')) return;
        this.panel.hidden = true;
        this.backdrop.hidden = true;
      }, 300);
      this.previousFocus?.focus();
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      if (!document.body.classList.contains('size-suggestions-is-open')) return;
      this.close();
    }

    /* Results ------------------------------------------------------------ */

    async load(size, context, key) {
      if (this.cache.has(key)) {
        this.render(this.cache.get(key));
        return;
      }

      this.setLoading(true);
      this.controller?.abort();
      this.controller = new AbortController();

      const query = {
        size,
        colors: this.split(context, 'colors'),
        packs: this.split(context, 'packs'),
        categories: this.split(context, 'categories'),
        productType: context?.dataset.productType || '',
      };

      const seen = new Set([context?.dataset.handle]);
      const items = [];
      const requested = new Set();

      try {
        for (const tier of TIERS) {
          if (items.length >= this.limit) break;

          const url = this.buildUrl(query, tier);
          if (!url || requested.has(url)) continue;
          requested.add(url);

          const found = await this.fetchTier(url);
          found.forEach((item) => {
            const handle = item.dataset.handle;
            if (seen.has(handle)) return;
            if (!this.sameDepartment(item, query.categories)) return;
            seen.add(handle);
            items.push(item);
          });
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.setLoading(false);
        this.render([]);
        return;
      }

      const ranked = this.rank(items, query).slice(0, this.limit);
      this.cache.set(key, ranked);
      this.setLoading(false);
      this.render(ranked);
    }

    buildUrl(query, tier) {
      if (tier.color && !query.colors.length) return null;
      if (tier.pack && !query.packs.length) return null;
      if (tier.type && !query.productType) return null;
      if (tier.needsCategory && !query.categories.length) return null;

      const params = new URLSearchParams();
      params.append('section_id', this.dataset.sectionId);
      params.append('sort_by', 'best-selling');
      params.append(this.dataset.sizeParam, query.size);
      params.append('filter.v.availability', '1');

      /*
       * The department, on every tier. When the product has no category value
       * there is nothing to hold on to, so the tiers that survive are the ones
       * still pinned to the product type.
       */
      query.categories.forEach((category) => params.append(this.dataset.categoryParam, category));

      /* A repeated filter is an OR, which is exactly "shares a color group". */
      if (tier.color) query.colors.forEach((color) => params.append(this.dataset.colorParam, color));
      if (tier.pack) query.packs.forEach((pack) => params.append(this.dataset.packParam, pack));
      if (tier.type) params.append(this.dataset.typeParam, query.productType);

      return `${this.dataset.collectionUrl}?${params.toString()}`;
    }

    /*
     * The category filter in the query is what keeps departments apart, and
     * this is the seatbelt. A filter parameter Shopify does not recognise is
     * silently ignored rather than rejected, and an ignored category filter
     * returns the whole catalog, so a wrong metafield key in the settings
     * would put mens boxers under a womens product with nothing failing
     * loudly. Both sides carry their departments, so the overlap is checked
     * again here on what actually came back.
     *
     * Only checked when both sides are known: an uncategorised product cannot
     * contradict anything, and dropping those would empty the drawer for the
     * handful of products that have no category at all.
     */
    sameDepartment(item, categories) {
      if (!categories.length) return true;
      const itemCategories = (item.dataset.categories || '').split('|').filter(Boolean);
      if (!itemCategories.length) return true;
      return itemCategories.some((category) => categories.includes(category));
    }

    async fetchTier(url) {
      const response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) return [];
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      return Array.from(html.querySelectorAll('[data-size-suggestion]'));
    }

    /*
     * Color first, pack second, and the tier order underneath both because the
     * array arrives narrow to wide. Products carry their own color groups on a
     * data attribute, so the overlap can be counted here rather than guessed
     * from which query returned them.
     */
    rank(items, query) {
      const colors = new Set(query.colors);
      const packs = new Set(query.packs);

      return items
        .map((item, index) => {
          const itemColors = (item.dataset.colors || '').split('|').filter(Boolean);
          return {
            item,
            index,
            overlap: itemColors.filter((color) => colors.has(color)).length,
            pack: packs.has(item.dataset.pack) ? 1 : 0,
          };
        })
        .sort((a, b) => b.overlap - a.overlap || b.pack - a.pack || a.index - b.index)
        .map((entry) => entry.item);
    }

    setLoading(state) {
      this.classList.toggle('size-suggestions--loading', state);
    }

    render(items) {
      const list = document.createElement('ul');
      list.className = 'size-suggestions__results';
      items.forEach((item) => list.appendChild(item));

      this.body.innerHTML = '';
      this.body.appendChild(list);
      if (this.empty) this.empty.hidden = items.length > 0;

      /*
       * Everything in a card arrives as inert markup: the theme binds its own
       * handlers at page load, long before these exist. Both of its
       * interactions have to be wired up again here.
       */

      /* Adding a size. Sold out ones are dead ends inside the panel. */
      list.querySelectorAll('.quick-view__option').forEach((option) => {
        if (option.querySelector('.quick-view-unavailable')) return;
        option.addEventListener('click', (event) => this.addToCart(event.currentTarget));
      });

      /*
       * Revealing the sizes on touch, where there is no hover to do it. Same
       * job as the theme's own handler, but scoped to the panel so opening one
       * card in here does not close a card on the page behind it.
       */
      list.querySelectorAll('.quick_view_button').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const card = event.currentTarget.closest('.product-card');
          list.querySelectorAll('.product-card.is-active').forEach((open) => {
            if (open !== card) open.classList.remove('is-active');
          });
          card?.classList.toggle('is-active');
        });
      });
    }

    /* Add to cart -------------------------------------------------------- */

    /*
     * Mirrors what the quick view does elsewhere: post the line, re-read the
     * cart for the count, then hand over to the theme's cart notification.
     * The loading and added classes are the theme's own, so the row animates
     * the way it does on a collection card.
     */
    async addToCart(option) {
      if (option.classList.contains('quick-add-loading')) return;
      option.classList.add('quick-add-loading');

      try {
        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: option.dataset.id, quantity: 1 }] }),
        });
        if (!response.ok) throw new Error(response.status);

        option.classList.add('added-to-cart');
        window.setTimeout(() => option.classList.remove('added-to-cart'), 3000);
        await this.refreshCart();
      } catch (error) {
        /* Left as it was, so the row can simply be tapped again. */
      } finally {
        option.classList.remove('quick-add-loading');
      }
    }

    async refreshCart() {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`);
      if (!response.ok) return;
      const cart = await response.json();

      const count = document.querySelector('.cart-count');
      if (count) count.innerHTML = cart.item_count > 0 ? cart.item_count : '';
      if (cart.item_count > 0) {
        document.querySelector('.cart-icon-bubble__count')?.classList.remove('hidden');
      }

      /*
       * The notification lives in product-quick-buy.js, which the layout loads
       * on every page. Guarded anyway, so the add still works if that ever
       * stops being true.
       */
      if (typeof window.cartNotification === 'function' && cart.items.length) {
        window.cartNotification(cart.items[0].id);
      }
    }
  }

  window.SizeSuggestions = SizeSuggestions;
  customElements.define('size-suggestions', SizeSuggestions);
}
